import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, GraduationCap, Printer, TriangleAlert } from 'lucide-react';
import { useGetMyResultsQuery, getApiError, EXAM_KIND_LABEL, type ReportCard } from '@/store/api';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { Mark } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const TERMS: Record<string, string> = {
  term_1: 'First term',
  term_2: 'Second term',
  term_3: 'Third term',
};

/** 1st, 2nd, 3rd, 4th — the way a report card says it. */
function ordinal(n: number): string {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/*
  ═════════════════════════════════════════════════════════════════════════════
  A PARENT'S REPORT CARDS

  What is deliberately not here
  ─────────────────────────────
  Other children's marks, and any exam still being marked. The API returns only
  published exams for this parent's own children, so there is nothing on this
  screen to filter — which is the right place for that rule, because a filter
  in a component is one refactor away from being dropped.

  Why position is shown with the class size
  ─────────────────────────────────────────
  "3rd" alone is unreadable: third of nine and third of forty-five are not the
  same news. Every rank on this page carries what it is out of.

  Why the subject list leads with the total, not the breakdown
  ────────────────────────────────────────────────────────────
  A parent wants to know how their child did. The class score and exam score
  are there — they answer the next question, which is why — but they are
  smaller, because leading with three numbers per subject makes a report card
  look like a spreadsheet and gets skimmed.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function ParentResultsPage() {
  const { data: children = [], isLoading, isError, error } = useGetMyResultsQuery();
  const [openId, setOpenId] = useState<string>('');

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="flex items-start gap-2 rounded-lg border border-destructive/25 bg-destructive/[0.06] px-3 py-2.5 text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]"
      >
        <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
        {getApiError(error)}
      </div>
    );
  }

  const anyReports = children.some((c) => c.reports.length > 0);

  if (!anyReports) {
    return (
      <EmptyState
        icon={<GraduationCap size={22} />}
        title="No results yet"
        description="Report cards appear here as soon as the school releases them at the end of a term."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {children.map((child) => (
        <section key={child.studentId} className="flex flex-col gap-2">
          <h2 className="text-[13px] font-bold tracking-tight text-foreground">{child.studentName}</h2>

          {child.reports.length === 0 ? (
            <p className={cn(styles.meta, 'rounded-lg border border-border bg-card px-3.5 py-3')}>
              Nothing released for {child.studentName.split(' ')[0]} yet.
            </p>
          ) : (
            child.reports.map((report) => {
              const key = `${child.studentId}:${report.examId}`;
              const open = openId === key;
              return (
                <article key={key} className="overflow-clip rounded-lg border border-border bg-card">
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? '' : key)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted"
                  >
                    <span className="flex h-11 w-11 flex-shrink-0 flex-col items-center justify-center rounded-md border border-success/30 bg-success/[0.09]">
                      <span className="text-[15px] font-bold leading-none text-[hsl(var(--record-ink-success))]">
                        {report.overallGrade}
                      </span>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-bold text-foreground">{report.examName}</p>
                      {/* Which assessment this was, so a mid-term and an
                          end-of-term in the same term are told apart without
                          reading the exam's name closely. */}
                      <p className={styles.meta}>
                        {EXAM_KIND_LABEL[report.kind] ?? ''} · {TERMS[report.term] ?? report.term} ·{' '}
                        {report.academicYear} · {report.studentClass}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-[15px] font-bold tabular-nums leading-none text-foreground">
                        {report.average}
                      </p>
                      <p className={cn(styles.meta, 'mt-0.5')}>average</p>
                    </div>
                  </button>

                  {open && (
                    <>
                      <ReportBody report={report} />
                      {/* A copy for a file, or for the next school. Reading it
                          on a phone is not a copy. */}
                      <div className="border-t border-border px-4 py-2.5">
                        <Link
                          to={`/parent/results/${report.examId}/${child.studentId}/print`}
                          className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-primary hover:underline"
                        >
                          <Printer size={13} />
                          Print or save as PDF
                        </Link>
                      </div>
                    </>
                  )}
                </article>
              );
            })
          )}
        </section>
      ))}
    </div>
  );
}

function ReportBody({ report }: { report: ReportCard }) {
  return (
    <div className="border-t border-border">
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
        <Figure label="Position" value={report.position > 0 ? ordinal(report.position) : '—'} hint={`of ${report.classSize}`} />
        <Figure label="Subjects" value={String(report.subjectsTaken)} hint="taken" />
        <Figure label="Total" value={String(report.totalScore)} hint={`over ${report.subjectsTaken} subjects`} />
      </div>

      <div className="divide-y divide-border">
        {report.subjects.map((s) => (
          <div key={s.subjectId} className="flex items-center gap-3 px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12.5px] font-semibold text-foreground">{s.subject}</p>
              {/* The breakdown answers "why", so it sits under the answer. */}
              <p className={styles.meta}>
                class {s.classScore} · exam {s.examScore} · {ordinal(s.position)} of {s.classSize}
              </p>
            </div>
            <span className="flex-shrink-0 text-[14px] font-bold tabular-nums text-foreground">{s.total}</span>
            <Mark tone={s.total >= 50 ? 'success' : 'warning'} className="w-[34px] justify-center">
              {s.grade}
            </Mark>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-2 border-t border-border bg-muted/40 px-4 py-3">
        <Award size={14} className="mt-[1px] flex-shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="text-[11.5px] font-bold text-foreground">{report.overallRemark}</p>
          {/* Whose words these are matters to a parent reading them. */}
          {report.classTeacherRemark && (
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
              <span className="font-bold">Class teacher:</span> {report.classTeacherRemark}
            </p>
          )}
          {report.headTeacherRemark && (
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
              <span className="font-bold">Head teacher:</span> {report.headTeacherRemark}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="px-3 py-2.5 text-center">
      <p className={styles.label}>{label}</p>
      <p className="mt-0.5 text-[16px] font-bold tabular-nums leading-none text-foreground">{value}</p>
      <p className={cn(styles.meta, 'mt-0.5')}>{hint}</p>
    </div>
  );
}
